import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form } from "react-bootstrap";
import {
  fetchSubjects,
  deleteSubject,
  clearError,
  clearSuccess,
} from "../../redux/slices/subjectSlice";
import { fetchClasses } from "../../redux/slices/classSlice";
import SubjectModal from "./SubjectModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import DynamicBadge from "../Common/DynamicBadge";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const DATATABLE_PAGINATION_OPTIONS = {
  initialState: { rowsPerPage: 10, options: [5, 10, 20, 50] }
};

const Subjects = () => {
  const dispatch = useDispatch();
  const { subjects = [], isLoading, error, success } = useSelector((state) => state.subjects);
  const { classes = [] } = useSelector((state) => state.classes);

  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState(null);
  const [classFilter, setClassFilter] = useState("");

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchClasses());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setShowModal(true);
  };

  const handleDeleteClick = (subject) => {
    setSubjectToDelete(subject);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (subjectToDelete) {
      dispatch(deleteSubject(subjectToDelete.id));
      setShowConfirmModal(false);
      setSubjectToDelete(null);
    }
  };

  const filteredSubjects = classFilter
    ? subjects.filter((s) => s.class_id === classFilter)
    : subjects;

  const header = [
    {
      prop: "subject_name",
      title: "Subject Name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">{row.subject_name}</span>
      )
    },
    {
      prop: "class_name",
      title: "Class",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.class_name || "Unknown"} />
      ),
    },
    {
        prop: "board_name",
        title: "Board",
        isSortable: true,
        isFilterable: true,
        cell: (row) => (
          <DynamicBadge text={row.board_name || "Unknown"} />
        ),
      },
    {
      prop: "createddate",
      title: "Created Date",
      isSortable: true,
      isFilterable: true,
      cell: (row) => formatDateToDDMMYYYY(row.createddate) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button variant="outline-dark" size="sm" onClick={() => handleEdit(row)}>
            <FaPencilAlt />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(row)}>
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading subjects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Subjects
        </Link>
      </div>

      <DatatableWrapper
        body={filteredSubjects}
        headers={header}
        paginationOptionsProps={DATATABLE_PAGINATION_OPTIONS}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "250px" }}>
              <Filter placeholder="Search subjects..." className="w-100" />
            </div>
            <div style={{ maxWidth: "200px" }}>
              <Form.Select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Classes</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.class_name} ({cls.board_name})
                  </option>
                ))}
              </Form.Select>
            </div>
            <PaginationOptions />
            <InfoPill
              left="Total Subjects"
              right={filteredSubjects?.length}
            />
          </div>

          <div className="d-flex align-items-center">
            <Button
              variant="dark"
              className="btn-sm"
              onClick={() => {
                setEditingSubject(null);
                setShowModal(true);
              }}
            >
              New Subject
            </Button>
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      <SubjectModal show={showModal} onHide={() => setShowModal(false)} subject={editingSubject} />

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Subject"
        message={`Are you sure you want to delete the subject "${subjectToDelete?.subject_name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isLoading}
      />
    </div>
  );
};

export default Subjects;
